import { FaArrowUp, FaBookOpen, FaChartLine, FaClock, FaHandsHelping, FaMoneyBill } from 'react-icons/fa';
import { images } from '../constants'; 

const careerIntro = {
    sub: "Join Our Team",
    heading: "Become a Part of Our Car Rental Team",
    description: "At OnDrive, we are looking for dedicated professionals to join our growing team. Whether you're passionate about customer service, vehicle maintenance, or fleet management, we offer exciting career opportunities in the car rental industry. Join us and help deliver exceptional experiences to our customers every day.",
    points: [
        "Flexible Schedules",
        "Competitive Salary",
        "Growth Opportunities",
        "Training Programs",
        "Dynamic Team",
        "Work with Modern Fleet",
    ],
};

const benefits = [
    {
        id: 1,
        title: "Career Advancement",
        description: "Explore exciting opportunities for career growth in the dynamic car rental industry.",
        icon: <FaChartLine />
    },
    {
        id: 2,
        title: "Comprehensive Training",
        description: "Gain hands-on experience with expert-led training in customer service & operations.",
        icon: <FaBookOpen />
    },
    {
        id: 3,
        title: "Competitive Pay",
        description: "Enjoy attractive salary packages with performance-based incentives and bonuses.",
        icon: <FaMoneyBill />
    },
    {
        id: 4,
        title: "Flexible Work Schedules",
        description: "Work with flexible shifts that suit your lifestyle and personal commitments.",
        icon: <FaClock />
    },
    {
        id: 5,
        title: "Supportive Work Environment",
        description: "Join a team that values collaboration, innovation, and customer satisfaction.",
        icon: <FaHandsHelping />
    },
    {
        id: 6,
        title: "Career Growth Potential",
        description: "Advance your career with promotions, skill development, and leadership opportunities.",
        icon: <FaArrowUp />
    }
];


const positions = [
    {
        id: 1,
        image: images.position1,
        title: 'Rental Sales Agent',
        content: 'Assist customers in selecting the best rental vehicles, process bookings, and provide top-notch customer service.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Customer Service Experience Preferred',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$40K - $50K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'Become a key part of our car rental operations by helping customers select vehicles, process bookings, and ensure a seamless rental experience. As a Rental Sales Agent, you will provide exceptional service and play a vital role in customer satisfaction and business success.'
        ],
        responsibilities: [
            'Assist customers in choosing appropriate rental vehicles based on their needs.',
            'Process bookings, returns, and payments accurately and efficiently.',
            'Provide detailed information on vehicle features, insurance options, and policies.',
            'Resolve customer queries and issues in a professional manner.',
            'Maintain records of transactions and ensure data accuracy.',
        ],
        qualifications: [
            'Previous experience in sales or customer service is an advantage.',
            'Excellent communication and interpersonal skills.',
            'Ability to handle customer concerns calmly and efficiently.',
            'Basic computer and data entry skills.',
            'Knowledge of vehicle types and rental procedures is a plus.',
        ],
    },
    {
        id: 2,
        image: images.position2,
        title: 'Customer Support',
        content: 'Handle customer inquiries, assist with reservations, and provide exceptional service to ensure client satisfaction.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Prior Experience in Customer Support Preferred',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$38K - $48K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'As a Customer Support representative, you’ll be the first point of contact for our clients. You’ll assist with inquiries, resolve concerns, manage reservations, and ensure every customer interaction reflects our commitment to excellence and satisfaction.'
        ],
        responsibilities: [
            'Respond to customer inquiries via phone, email, and in-person.',
            'Assist clients with booking reservations and modifying existing ones.',
            'Provide detailed information about rental policies and services.',
            'Handle complaints and resolve issues professionally and efficiently.',
            'Maintain customer records and follow up to ensure satisfaction.',
        ],
        qualifications: [
            'Experience in customer service or a call center environment preferred.',
            'Excellent verbal and written communication skills.',
            'Strong problem-solving abilities and attention to detail.',
            'Ability to multitask and manage time efficiently.',
            'Proficient with basic computer systems and reservation software.',
        ],
    },
    {
        id: 3,
        image: images.position3,
        title: 'Fleet Manager',
        content: 'Oversee vehicle maintenance, manage inventory, and ensure the availability of quality rental cars.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 3 Years in Fleet or Operations Management',
            position: '1 Position Available',
            days: 'Monday to Friday',
            salary: '$50K - $65K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'We’re looking for an experienced Fleet Manager to lead the operational efficiency of our rental vehicle inventory. You will be responsible for maintaining vehicle quality, coordinating repairs, monitoring usage, and ensuring optimal fleet performance to meet customer demand.'
        ],
        responsibilities: [
            'Manage daily operations of the vehicle fleet including assignments, maintenance, and logistics.',
            'Oversee inspections, repairs, and ensure all vehicles meet safety and cleanliness standards.',
            'Track fleet usage, mileage, and maintenance schedules using fleet management systems.',
            'Coordinate with vendors and service providers for timely vehicle servicing.',
            'Ensure availability of vehicles to meet customer demand and minimize downtime.',
        ],
        qualifications: [
            'Proven experience in fleet or operations management, preferably in the rental or transportation industry.',
            'Strong organizational and leadership skills.',
            'Knowledge of vehicle maintenance and repair processes.',
            'Familiarity with fleet tracking and management software.',
            'Ability to multitask and adapt in a fast-paced environment.',
        ],
    },
    {
        id: 4,
        image: images.position4,
        title: 'Vehicle Inspector',
        content: 'Inspect rental cars before and after use, ensuring they meet safety and cleanliness standards.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 1 Year in Vehicle Inspection or Similar Role',
            position: 'Multiple Positions Available',
            days: 'Monday to Saturday',
            salary: '$35K - $45K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'We’re hiring a detail-oriented Vehicle Inspector to examine rental vehicles before and after customer use. This role ensures vehicles are road-ready, safe, clean, and meet company standards for quality and presentation.'
        ],
        responsibilities: [
            'Perform thorough inspections of vehicles before and after rental periods.',
            'Document existing and new damages using inspection checklists or digital tools.',
            'Ensure vehicles meet safety, operational, and cleanliness standards.',
            'Report any issues requiring maintenance or repair to the appropriate department.',
            'Collaborate with cleaning and maintenance teams to prepare vehicles for the next rental.'
        ],
        qualifications: [
            'Experience in vehicle inspection, detailing, or automotive services.',
            'Attention to detail with a focus on safety and quality.',
            'Basic knowledge of vehicle components and safety standards.',
            'Strong observational and documentation skills.',
            'Ability to work independently and in a team environment.',
        ],
    },
    {
        id: 5,
        image: images.position5,
        title: 'Operation Coordinator',
        content: 'Monitor fleet availability, manage schedules, and ensure smooth operations within the rental service.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 2 Years in Operations or Fleet Management',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$45K - $55K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'We are looking for an organized and proactive Operation Coordinator to oversee daily activities in our car rental operations. This role ensures seamless coordination of schedules, vehicle availability, and customer service excellence.'
        ],
        responsibilities: [
            'Monitor and manage fleet availability to meet customer demands.',
            'Coordinate daily schedules for vehicle pick-ups, returns, and maintenance.',
            'Communicate with rental agents, technicians, and drivers to ensure smooth workflows.',
            'Track performance metrics and identify areas for operational improvements.',
            'Handle escalations, delays, or unexpected operational issues swiftly and effectively.'
        ],
        qualifications: [
            'Experience in operations, logistics, or fleet coordination roles.',
            'Strong organizational and multitasking skills.',
            'Familiarity with scheduling software and fleet tracking systems.',
            'Excellent communication and problem-solving abilities.',
            'Ability to work under pressure and adapt to changing priorities.'
        ],
    },
    {
        id: 6,
        image: images.position6,
        title: 'Delivery Specialist',
        content: 'Deliver and pick up rental vehicles from customers, ensuring timely and professional service.',
        details: {
            location: 'On-site',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 1 Year in Delivery or Transportation Services',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$36K - $46K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'We’re hiring a Delivery Specialist to ensure seamless transportation of rental vehicles to and from our clients. If you take pride in punctuality and professionalism, this is a great opportunity to be part of a customer-first rental team.'
        ],
        responsibilities: [
            'Safely deliver rental vehicles to customers and retrieve them upon return.',
            'Inspect vehicles for any damages before and after delivery.',
            'Provide friendly and professional interactions with customers during vehicle handoffs.',
            'Ensure timely pick-ups and drop-offs according to schedules.',
            'Maintain accurate records of deliveries and vehicle status.'
        ],
        qualifications: [
            'Valid driver’s license with a clean driving record.',
            'Previous experience in vehicle delivery or transportation is preferred.',
            'Excellent time management and route planning skills.',
            'Customer service-oriented with a professional demeanor.',
            'Ability to work independently and handle multiple deliveries daily.'
        ],
    }

];

export { positions, benefits, careerIntro };